import type { ContainerPageRow } from './container-page-rows';
import { displayOcrStatus, type DisplayOcrStatus } from './run-status';

/**
 * What a row says about ONE page, which is not the same thing as the status of
 * the run that owns it.
 *
 * A run covers many pages and reports one status, so reading that status per
 * page is what let a refusal on page 8 render "failed" onto pages 7 and 9-12.
 * `coverage` separates the three real cases; only `extracted` may borrow the
 * run's own status.
 */
export type PageRowState = DisplayOcrStatus | 'no_data' | 'not_covered';

export function resolvePageRowState(row: ContainerPageRow): PageRowState {
  if (row.coverage === 'extracted') return displayOcrStatus(row.result.status);

  // Nothing claims this page, so the owning row is only a handle — its status
  // describes work done somewhere else entirely.
  if (row.coverage === 'uncovered') return 'not_covered';

  // Assigned and empty. A run that ENDED badly is still the honest explanation
  // for its own assigned page — that is the refusal case, and it must keep
  // saying so. A run that completed has no failure to report: the page was read
  // and came back with nothing, which is a blank scan or a batch the model
  // answered empty, not an error anyone raised.
  const status = displayOcrStatus(row.result.status);

  return status === 'failed' || status === 'processing' || status === 'paused'
    ? status
    : 'no_data';
}
